import React, { useRef, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Button, { BUTTON_VARIANT_PRIMARY, BUTTON_VARIANT_OUTLINE } from '@components/1-atoms/Button';
import Switch from '@components/1-atoms/Switch';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import { usePageTitle } from '@contexts/usePageTitle';
import { useAppsFooter } from '@components/AppsFooter/useAppsFooter';
import {
  listAgentSkills,
  createAgentSkill,
  updateAgentSkill,
  deleteAgentSkill,
  setAgentSkillEnabled,
  skillInUseOf,
  skillKey,
} from '@api/agentSkills';
import type { AgentSkill, AgentSkillRequest, AgentSkillsListResponse, SkillInUse } from '@api/agentSkills';
import { axiosErrorMessage } from '@api/errors';
import { agentDisplayName } from '@constants/agentNames';
import { PageContainer, PageContent } from '../Page.styled';
import {
  Description,
  SectionHeader,
  SectionTitle,
  SkillList,
  SkillCard,
  SkillInfo,
  SkillNameRow,
  SkillName,
  SkillId,
  BuiltInBadge,
  SkillDescription,
  SkillTools,
  SkillActions,
  EmptyState,
  StateContainer,
  StaleBanner,
  StaleBannerText,
  StaleList,
  StaleRow,
  StaleSkillId,
} from './SkillsPage.styled';
import SkillFormDialog from './SkillFormDialog';
import type { SelectableTool, ToolRunner } from './SkillFormDialog';
import BuiltInSkillDialog from './BuiltInSkillDialog';
import SkillInUseDialog from './SkillInUseDialog';
import { useEntitlements } from '@hooks/useEntitlements';
import useAuth from '@contexts/useAuth';
import { nodeDefinitionsQueryKey } from '../Processes/hooks/useNodeDefinitions';

const SKILLS_QUERY_KEY = ['agentSkills'];

type DialogState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; skill: AgentSkill };

interface SaveVars {
  request: AgentSkillRequest;
  force: boolean;
}

interface SaveInUse {
  inUse: SkillInUse;
  request: AgentSkillRequest;
}

interface DeleteVars {
  skillId: string;
  force: boolean;
}

interface ToggleVars {
  key: string;
  enabled: boolean;
}

interface ToggleContext {
  /** Undefined when the key belongs to a leftover switch rather than a card. */
  previousEnabled: boolean | undefined;
  wasStale: boolean;
}

/**
 * Every other skill keeps its object identity, so a toggle only re-renders its
 * own card. A key matching no card is a leftover switch, cleared by enabling it.
 */
function withSkillToggled(
  data: AgentSkillsListResponse,
  key: string,
  enabled: boolean,
): AgentSkillsListResponse {
  const stale = data.staleDisabledSkillIds ?? [];
  return {
    ...data,
    skills: data.skills.map((s) => (skillKey(s) === key ? { ...s, enabled } : s)),
    staleDisabledSkillIds: enabled ? stale.filter((id) => id !== key) : stale,
  };
}

/**
 * Reverts one key rather than restoring a whole-list snapshot, so a rejected
 * toggle cannot discard a switch the user flipped while it was in flight.
 */
function withSkillToggleReverted(
  data: AgentSkillsListResponse,
  key: string,
  { previousEnabled, wasStale }: ToggleContext,
): AgentSkillsListResponse {
  const stale = data.staleDisabledSkillIds ?? [];
  return {
    ...data,
    skills:
      previousEnabled === undefined
        ? data.skills
        : data.skills.map((s) => (skillKey(s) === key ? { ...s, enabled: previousEnabled } : s)),
    staleDisabledSkillIds: wasStale && !stale.includes(key) ? [...stale, key] : stale,
  };
}

/**
 * A tool is restricted to one runner type only when *every* skill shipping it
 * is hidden on the other — ``vectorize_scan`` comes with both the portable
 * ``vectorize_scan`` and ``drawing_to_3d``, so it stays unrestricted.
 */
function runnerFor(declaringSkills: AgentSkill[]): ToolRunner {
  if (declaringSkills.every((s) => s.hiddenOn?.includes('cloud'))) {
    return 'local';
  }
  if (declaringSkills.every((s) => s.hiddenOn?.includes('local'))) {
    return 'cloud';
  }
  return 'any';
}

/**
 * The tools a custom skill may unlock: everything the org's entitled built-ins
 * declare, badged with the runner type each one needs.
 */
function selectableTools(builtInSkills: AgentSkill[]): SelectableTool[] {
  const declaredBy = new Map<string, AgentSkill[]>();
  builtInSkills.forEach((skill) =>
    skill.tools.forEach((tool) => {
      const declaring = declaredBy.get(tool);
      if (declaring) {
        declaring.push(skill);
      } else {
        declaredBy.set(tool, [skill]);
      }
    }),
  );
  return Array.from(declaredBy.entries())
    .map(([name, declaring]) => ({ name, runner: runnerFor(declaring) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

const SkillsPage: React.FC = () => {
  const intl = useIntl();
  usePageTitle(
    intl.formatMessage({ id: 'skills.title', defaultMessage: 'Skills' }),
    intl.formatMessage({
      id: 'skills.help',
      defaultMessage:
        'Instruction bundles the agent loads when a task needs them. Use the built-in ones, write your own, or share one with your organization.',
    }),
  );
  const queryClient = useQueryClient();
  const { showMarketplace } = useAppsFooter();
  const { user } = useAuth();
  const isAibuildAdmin = user?.orgRole === 'AIBUILD_ADMIN';
  const {
    isGateOpen,
    isLoading: gatesLoading,
    isError: gatesError,
  } = useEntitlements();

  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  const [deleteTarget, setDeleteTarget] = useState<AgentSkill | null>(null);
  const [deleteInUse, setDeleteInUse] = useState<SkillInUse | null>(null);
  const [saveInUse, setSaveInUse] = useState<SaveInUse | null>(null);
  const [viewBuiltIn, setViewBuiltIn] = useState<AgentSkill | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: SKILLS_QUERY_KEY,
    queryFn: listAgentSkills,
    staleTime: 60_000,
  });

  const closeDialog = () => {
    setSaveInUse(null);
    setDialog({ kind: 'closed' });
  };

  const saveMutation = useMutation({
    mutationFn: async ({ request, force }: SaveVars) => {
      if (dialog.kind === 'edit' && dialog.skill.id) {
        return updateAgentSkill(dialog.skill.id, request, force);
      }
      return createAgentSkill(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: nodeDefinitionsQueryKey });
      closeDialog();
    },
    onError: (err, { request }) => {
      const inUse = skillInUseOf(err);
      if (inUse) setSaveInUse({ inUse, request });
    },
  });

  const cancelSaveInUse = () => {
    setSaveInUse(null);
    saveMutation.reset();
  };

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteInUse(null);
  };

  const deleteMutation = useMutation({
    mutationFn: ({ skillId, force }: DeleteVars) => deleteAgentSkill(skillId, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: nodeDefinitionsQueryKey });
      closeDelete();
    },
    onError: (err) => {
      const inUse = skillInUseOf(err);
      if (inUse) setDeleteInUse(inUse);
      else closeDelete();
    },
  });

  // Enabling deletes a row and disabling writes one, so two writes for the same
  // skill in flight together let the slower one decide the outcome. Queue them
  // per skill; different skills still save in parallel.
  const toggleQueues = useRef(new Map<string, Promise<unknown>>());
  const toggleBaselines = useRef(new Map<string, ToggleContext>());
  // Counted here rather than read back from the mutation cache: two toggles
  // settling in the same tick still see each other as pending there, so both
  // would defer the refetch to the other and the list would never reconcile.
  const pendingToggles = useRef(new Map<string, number>());

  const countToggle = (key: string, delta: number) => {
    const remaining = (pendingToggles.current.get(key) ?? 0) + delta;
    if (remaining > 0) pendingToggles.current.set(key, remaining);
    else pendingToggles.current.delete(key);
    return remaining;
  };

  const enabledMutation = useMutation<void, Error, ToggleVars, ToggleContext>({
    mutationFn: ({ key, enabled }) => {
      const queued = toggleQueues.current
        .get(key)
        ?.catch(() => undefined)
        .then(() => setAgentSkillEnabled(key, enabled));
      const next = queued ?? setAgentSkillEnabled(key, enabled);
      toggleQueues.current.set(key, next);
      return next;
    },
    onMutate: async ({ key, enabled }) => {
      countToggle(key, 1);
      await queryClient.cancelQueries({ queryKey: SKILLS_QUERY_KEY });
      const previous = queryClient.getQueryData<AgentSkillsListResponse>(SKILLS_QUERY_KEY);
      queryClient.setQueryData<AgentSkillsListResponse>(SKILLS_QUERY_KEY, (data) =>
        data ? withSkillToggled(data, key, enabled) : data,
      );
      // Only the first click of a burst sees server-confirmed state; the ones
      // behind it read a cache this burst has already rewritten, so they would
      // roll back to an optimistic value the server never held.
      const baseline = toggleBaselines.current.get(key) ?? {
        previousEnabled: previous?.skills.find((s) => skillKey(s) === key)?.enabled,
        wasStale: (previous?.staleDisabledSkillIds ?? []).includes(key),
      };
      toggleBaselines.current.set(key, baseline);
      return baseline;
    },
    onError: (_err, { key }, context) => {
      // A queued toggle for this skill supersedes this one; let it settle the card.
      if (!context || (pendingToggles.current.get(key) ?? 0) > 1) return;
      queryClient.setQueryData<AgentSkillsListResponse>(SKILLS_QUERY_KEY, (data) =>
        data ? withSkillToggleReverted(data, key, context) : data,
      );
    },
    onSettled: (_data, _err, { key }) => {
      if (countToggle(key, -1) === 0) {
        toggleQueues.current.delete(key);
        toggleBaselines.current.delete(key);
      }
      // Invalidating while a sibling toggle is in flight would overwrite its
      // optimistic state with a list the server has not saved yet.
      if (pendingToggles.current.size > 0) return;
      queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEY });
    },
  });

  const skills = data?.skills ?? [];
  const staleSkillIds = data?.staleDisabledSkillIds ?? [];
  const builtInSkills = skills.filter((s) => s.builtIn);
  const orgSkills = skills.filter((s) => !s.builtIn && s.shared);
  // The API only ever returns the caller's own individual skills, so everything
  // unshared here belongs to the signed-in user.
  const personalSkills = skills.filter((s) => !s.builtIn && !s.shared);

  const availableTools = selectableTools(
    builtInSkills.filter((s) => !s.requiredGate || isGateOpen(s.requiredGate)),
  );

  const openCreate = () => {
    saveMutation.reset();
    setDialog({ kind: 'create' });
  };
  const openEdit = (skill: AgentSkill) => {
    saveMutation.reset();
    setDialog({ kind: 'edit', skill });
  };

  // Never pass `disabled`: a disabled Switch renders identically to an off one,
  // so gating on a request in flight makes every switch look like it turned off.
  const renderEnabledSwitch = (skill: AgentSkill) => {
    const key = skillKey(skill);
    return (
      <Switch
        enabled={skill.enabled}
        onChange={(enabled: boolean) => enabledMutation.mutate({ key, enabled })}
        dataTestId={`skill-toggle-${skill.name}`}
        ariaLabel={intl.formatMessage(
          { id: 'skills.toggle.label', defaultMessage: 'Use the {name} skill' },
          { name: agentDisplayName(skill.name) },
        )}
      />
    );
  };

  const renderCustomSkill = (skill: AgentSkill) => (
    <SkillCard key={skill.id} $off={!skill.enabled}>
      <SkillInfo>
        <SkillNameRow>
          <SkillName>{agentDisplayName(skill.name)}</SkillName>
          <SkillId>{skill.name}</SkillId>
        </SkillNameRow>
        <SkillDescription>{skill.description}</SkillDescription>
        {skill.tools.length > 0 && (
          <SkillTools>
            {intl.formatMessage(
              { id: 'skills.builtIn.tools', defaultMessage: 'Tools: {tools}' },
              { tools: skill.tools.join(', ') },
            )}
          </SkillTools>
        )}
      </SkillInfo>
      <SkillActions>
        {skill.manageable && (
          <>
            <Button
              variant={BUTTON_VARIANT_OUTLINE}
              onClick={() => openEdit(skill)}
              stretch={false}
            >
              {intl.formatMessage({ id: 'skills.custom.edit', defaultMessage: 'Edit' })}
            </Button>
            <Button
              variant={BUTTON_VARIANT_OUTLINE}
              onClick={() => setDeleteTarget(skill)}
              stretch={false}
            >
              {intl.formatMessage({ id: 'skills.custom.delete', defaultMessage: 'Delete' })}
            </Button>
          </>
        )}
        {renderEnabledSwitch(skill)}
      </SkillActions>
    </SkillCard>
  );

  if (isLoading) {
    return (
      <PageContainer>
        <PageContent>
          <StateContainer>
            <FormattedMessage id="skills.loading" defaultMessage="Loading skills..." />
          </StateContainer>
        </PageContent>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <PageContent>
          <StateContainer $error>
            <FormattedMessage
              id="skills.loadError"
              defaultMessage="Failed to load skills. Please try again later."
            />
          </StateContainer>
        </PageContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageContent data-testid="skills-page">
        <Description>
          <FormattedMessage
            id="skills.description"
            defaultMessage="Skills are instruction bundles the AI agent can load on demand. Built-in skills ship with the platform and are read-only. Your own skills teach your agent your workflows without affecting anyone else, and you can share one with the whole organization — it reaches every colleague switched off, for each of them to turn on if they want it."
          />
        </Description>

        <SectionHeader>
          <SectionTitle>
            <FormattedMessage id="skills.personal.title" defaultMessage="Your skills" />
          </SectionTitle>
          <Button variant={BUTTON_VARIANT_PRIMARY} onClick={openCreate} stretch={false}>
            {intl.formatMessage({ id: 'skills.custom.new', defaultMessage: 'New Skill' })}
          </Button>
        </SectionHeader>
        {personalSkills.length > 0 ? (
          <SkillList>{personalSkills.map(renderCustomSkill)}</SkillList>
        ) : (
          <EmptyState>
            <FormattedMessage
              id="skills.personal.empty"
              defaultMessage="No skills of your own yet. Create one to give your agent reusable instructions — only you will see it."
            />
          </EmptyState>
        )}

        <SectionHeader>
          <SectionTitle>
            <FormattedMessage id="skills.custom.title" defaultMessage="Organization skills" />
          </SectionTitle>
        </SectionHeader>
        {orgSkills.length > 0 ? (
          <>
            <Description $compact data-testid="skills-org-optin-hint">
              <FormattedMessage
                id="skills.custom.optIn"
                defaultMessage="Skills shared with your organization start switched off. Turn one on when you want your agent to use it — nobody else is affected either way."
              />
            </Description>
            <SkillList>{orgSkills.map(renderCustomSkill)}</SkillList>
          </>
        ) : (
          <EmptyState>
            <FormattedMessage
              id="skills.custom.empty"
              defaultMessage="No organization skills yet. Create one and share it to give every member's agent the same instructions, switched off until each of them turns it on."
            />
          </EmptyState>
        )}

        <SectionHeader>
          <SectionTitle>
            <FormattedMessage id="skills.builtIn.title" defaultMessage="Built-in skills" />
          </SectionTitle>
        </SectionHeader>
        <SkillList>
          {builtInSkills.map((skill) => {
            // Only lock once entitlements have resolved — while they load or if
            // the request failed we can't know the gate is closed, so avoid a
            // false (and, on failure, permanent) "Locked" for entitled users.
            const gatesResolved = !gatesLoading && !gatesError;
            const locked =
              !!skill.requiredGate && gatesResolved && !isGateOpen(skill.requiredGate);
            const canViewGuidance = isAibuildAdmin && !!skill.instructions;
            return (
              <SkillCard key={skill.name} $off={!skill.enabled}>
                <SkillInfo>
                  <SkillNameRow>
                    <SkillName>{agentDisplayName(skill.name)}</SkillName>
                    <SkillId>{skill.name}</SkillId>
                    <BuiltInBadge>
                      {locked ? (
                        <FormattedMessage id="skills.locked.badge" defaultMessage="Locked" />
                      ) : (
                        <FormattedMessage id="skills.builtIn.badge" defaultMessage="Built-in" />
                      )}
                    </BuiltInBadge>
                  </SkillNameRow>
                  <SkillDescription>{skill.description}</SkillDescription>
                  {skill.tools.length > 0 && (
                    <SkillTools>
                      {intl.formatMessage(
                        { id: 'skills.builtIn.tools', defaultMessage: 'Tools: {tools}' },
                        { tools: skill.tools.join(', ') },
                      )}
                    </SkillTools>
                  )}
                </SkillInfo>
                <SkillActions>
                  {canViewGuidance && (
                    <Button
                      variant={BUTTON_VARIANT_OUTLINE}
                      onClick={() => setViewBuiltIn(skill)}
                      stretch={false}
                    >
                      {intl.formatMessage({
                        id: 'skills.builtIn.view',
                        defaultMessage: 'View',
                      })}
                    </Button>
                  )}
                  {locked && (
                    <Button
                      variant={BUTTON_VARIANT_OUTLINE}
                      onClick={showMarketplace}
                      stretch={false}
                    >
                      {intl.formatMessage({
                        id: 'skills.locked.cta',
                        defaultMessage: 'Unlock in Marketplace',
                      })}
                    </Button>
                  )}
                  {renderEnabledSwitch(skill)}
                </SkillActions>
              </SkillCard>
            );
          })}
        </SkillList>

        {staleSkillIds.length > 0 && (
          <StaleBanner data-testid="stale-skills-banner">
            <StaleBannerText>
              <FormattedMessage
                id="skills.stale.description"
                defaultMessage="You set a switch on these skills, but they no longer exist. Clearing a leftover only removes the setting."
              />
            </StaleBannerText>
            <StaleList>
              {staleSkillIds.map((staleId) => (
                <StaleRow key={staleId}>
                  <StaleSkillId>{staleId}</StaleSkillId>
                  <Button
                    variant={BUTTON_VARIANT_OUTLINE}
                    onClick={() => enabledMutation.mutate({ key: staleId, enabled: true })}
                    stretch={false}
                  >
                    {intl.formatMessage({ id: 'skills.stale.clear', defaultMessage: 'Clear' })}
                  </Button>
                </StaleRow>
              ))}
            </StaleList>
          </StaleBanner>
        )}

        {viewBuiltIn && (
          <BuiltInSkillDialog skill={viewBuiltIn} onClose={() => setViewBuiltIn(null)} />
        )}

        {dialog.kind !== 'closed' && (
          <SkillFormDialog
            skill={dialog.kind === 'create' ? null : dialog.skill}
            availableTools={availableTools}
            isSaving={saveMutation.isPending}
            serverError={
              saveMutation.isError && !saveInUse
                ? axiosErrorMessage(saveMutation.error, 'Something went wrong. Please try again.')
                : null
            }
            onClose={closeDialog}
            onSubmit={(request) => saveMutation.mutate({ request, force: false })}
          />
        )}

        {saveInUse && (
          <SkillInUseDialog
            skillName={saveInUse.request.name}
            action="unshare"
            inUse={saveInUse.inUse}
            loading={saveMutation.isPending}
            onConfirm={() => saveMutation.mutate({ request: saveInUse.request, force: true })}
            onCancel={cancelSaveInUse}
          />
        )}

        {deleteTarget && deleteInUse && (
          <SkillInUseDialog
            skillName={deleteTarget.name}
            action="delete"
            inUse={deleteInUse}
            loading={deleteMutation.isPending}
            onConfirm={() => deleteTarget.id && deleteMutation.mutate({ skillId: deleteTarget.id, force: true })}
            onCancel={closeDelete}
          />
        )}

        <ConfirmDialog
          show={deleteTarget !== null && deleteInUse === null}
          title={intl.formatMessage({ id: 'skills.delete.title', defaultMessage: 'Delete Skill' })}
          confirmLabel={intl.formatMessage({ id: 'skills.delete.confirm', defaultMessage: 'Delete' })}
          loading={deleteMutation.isPending}
          onConfirm={() => deleteTarget?.id && deleteMutation.mutate({ skillId: deleteTarget.id, force: false })}
          onCancel={closeDelete}
        >
          <p>
            {intl.formatMessage(
              {
                id: 'skills.delete.message',
                defaultMessage: 'Delete skill "{name}"? The agent will no longer be able to load it.',
              },
              { name: deleteTarget?.name ?? '' },
            )}
          </p>
        </ConfirmDialog>
      </PageContent>
    </PageContainer>
  );
};

export default SkillsPage;
